import {
  Autocomplete,
  Card,
  CircularProgress,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import FormField from "@/components/atoms/FormField";
import { BrazilPhoneMaskInput } from "@/components/atoms/MaskInput";
import MDBox from "@/components/atoms/MDBox";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { OrderFormData } from "@/components/screens/OrderFormPage/types";
import { GENDER_OPTIONS } from "@/constants/gender.constants";
import { applyDocumentMask } from "@/helpers/document-helpers";
import { handlePhoneChanges } from "@/helpers/phone-helpers";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import useGetContacts from "@/hooks/requests/contact/use-get-contacts";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import IContact from "@/interfaces/contact.interface";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrderDataTabMessages } from "@/messages/order-form-page/order-data-tab.messages";
import { FilterType } from "@/types/filter.types";

type Props = {
  messages: OrderDataTabMessages;
  order: IOrder | null;
};

export default function Customer({ messages, order }: Props) {
  const { values, errors, setFieldValue, handleChange } = useFormikContext<OrderFormData>();
  const { filters, changeFilters } = useFilters<IContact>({ initialValue: [] });
  const pagination = usePagination();
  const currentPhoneNumber = removeNonIntegersFromString(values.shippingPhoneNumber);
  const isPaused = currentPhoneNumber === "" || currentPhoneNumber.length < 10;
  const { data: contacts, isLoading } = useGetContacts({ filters, isPaused, ...pagination });

  const genderOptions = GENDER_OPTIONS(messages);

  useEffect(() => {
    if (!isPaused) {
      const ddiMobile = currentPhoneNumber.substring(0, 2);
      const dddMobile = currentPhoneNumber.substring(2, 4);
      const mobilePhone = currentPhoneNumber.substring(4);

      changeFilters([
        { property: "ddiMobile", value: ddiMobile, filterType: FilterType.Equals },
        { property: "dddMobile", value: dddMobile, filterType: FilterType.Equals },
        { property: "mobilePhone", value: mobilePhone, filterType: FilterType.Equals },
      ]);
    }
  }, [currentPhoneNumber, changeFilters, isPaused]);

  useEffect(() => {
    if (contacts && contacts.items.length > 0) {
      const contact = contacts.items[0];

      const normalizedPhone = order ? removeNonIntegersFromString(order.shippingPhoneNumber) : "";

      if (!values.isEditing || (values.isEditing && currentPhoneNumber !== normalizedPhone)) {
        const addressDistrict = contact.addressDistrict || values.shippingAddressDistrict;
        const address = contact.addressZipCode || values.shippingAddressZipcode;
        const document = contact.document || values.shippingDocument;

        setFieldValue("contactId", contact.id);
        setFieldValue("shippingName", `${contact.firstName} ${contact.lastName}`);
        setFieldValue("shippingGender", contact.gender);
        setFieldValue("shippingEmail", contact.email);
        setFieldValue("shippingDocument", document);

        setFieldValue("shippingAddressState", contact.addressState || values.shippingAddressState);
        setFieldValue("shippingAddressCity", contact.addressCity || values.shippingAddressCity);
        setFieldValue("shippingAddressDistrict", addressDistrict);
        setFieldValue("shippingAddress", contact.address || values.shippingAddress);
        setFieldValue("shippingAddressZipcode", address);
      }
    }
  }, [
    contacts,
    setFieldValue,
    currentPhoneNumber,
    values.isEditing,
    values.shippingDocument,
    order,
    values.shippingAddressState,
    values.shippingAddressCity,
    values.shippingAddressDistrict,
    values.shippingAddress,
    values.shippingAddressZipcode,
    values.orderNumber,
  ]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.groupCustomerTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl variant="standard" fullWidth error={!!errors.shippingPhoneNumber}>
                <InputLabel htmlFor="formatted-text-mask-input">
                  {messages.shippingPhoneNumberLabel}
                </InputLabel>
                <Input
                  value={values.shippingPhoneNumber || ""}
                  onChange={(e) =>
                    handlePhoneChanges("shippingPhoneNumber", e.target.value, setFieldValue)
                  }
                  name="shippingPhoneNumber"
                  inputComponent={BrazilPhoneMaskInput}
                />
                {errors.shippingPhoneNumber && (
                  <FormHelperText error>{String(errors.shippingPhoneNumber)}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingName"
                label={messages.shippingNameLabel}
                value={values.shippingName || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.shippingName}
                disabled={values.disabledButton}
                required
                InputProps={{
                  endAdornment: isLoading && <CircularProgress size={"medium"} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Autocomplete
                fullWidth
                disabled={values.disabledButton}
                options={genderOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={
                  genderOptions.find((option) => option.value === values.shippingGender) || null
                }
                onChange={(_, newValue) =>
                  setFieldValue("shippingGender", newValue ? newValue.value : "")
                }
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label={messages.shippingGenderLabel}
                    variant="standard"
                    error={!!errors.shippingGender}
                    helperText={errors.shippingGender}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {isLoading && <CircularProgress size="medium" />}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <InputMaskWrapper
                mask={applyDocumentMask(values.shippingDocument || "")}
                dynamicMask
                value={values.shippingDocument || ""}
                onChange={handleChange}
                disabled={values.disabledButton}
                required={!values.isEditing}
                variant="standard"
                name="shippingDocument"
                label={messages.documentLabel}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.shippingDocument}
                helperText={errors.shippingDocument}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormField
                variant="standard"
                name="shippingEmail"
                label={messages.shippingEmailLabel}
                value={values.shippingEmail || ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.shippingEmail}
                disabled={values.disabledButton}
                inputProps={{
                  endAdornment: isLoading && <CircularProgress size={"medium"} />,
                }}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
